import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import { forwardRef, useId, useImperativeHandle, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import MButton from '../../MButton';
import MCustomIconButton from '../../MCustomIconButton';
import { MCustomSelect } from '../../MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../../MCustomSelect/components/MainSearchInputV2';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import { MFlex } from '../../chakra';
import {
  DataTableFilterRef,
  FilterProps,
  RecordSearchFilterData,
} from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

export const DataTableHeaderRecordSearchFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table }, ref) => {
  const key = getColumnFilterKey(column);
  const { open, onOpen, onClose } = useDisclosure();
  const recordSearch = column.columnDef.meta?.recordSearch;

  const getInitialState = (): RecordSearchFilterData | null => {
    const existingFilter = table.options.meta?.selectedFilters?.get(key);
    if (
      existingFilter &&
      typeof existingFilter === 'object' &&
      'id' in existingFilter
    ) {
      return existingFilter as RecordSearchFilterData;
    }
    return null;
  };

  const [appliedState, setAppliedState] =
    useState<RecordSearchFilterData | null>(getInitialState);
  const [selectedState, setSelectedState] =
    useState<RecordSearchFilterData | null>(getInitialState);
  const titleId = useId();

  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: () => {
      handleResetFilters();
    },
    setFilterValue: ({ columnKey, value }) => {
      if (columnKey === column.id) {
        column.setFilterValue(value);
      }
    },
  }));

  if (!recordSearch) {
    return null;
  }

  const handleResetFilters = () => {
    setSelectedState(null);
    setAppliedState(null);
    column.setFilterValue(undefined);

    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, undefined);
    }

    onClose();
  };

  const handleApplyFilters = () => {
    setAppliedState(selectedState);

    if (selectedState) {
      column.setFilterValue(selectedState);
      if (table.options.meta?.onFilterUpdate) {
        table.options.meta.onFilterUpdate(key, selectedState);
      }
    } else {
      column.setFilterValue(undefined);
      if (table.options.meta?.onFilterUpdate) {
        table.options.meta.onFilterUpdate(key, undefined);
      }
    }

    onClose();
  };

  const handleClose = () => {
    setSelectedState(appliedState);
    onClose();
  };

  const filtersApplied = !!appliedState?.id;
  const hasChanges = selectedState?.id !== (appliedState?.id ?? undefined);

  const { itemTitle, itemValue } = recordSearch;
  const searchParams = recordSearch.sort
    ? { sort: recordSearch.sort, ...recordSearch.additionalSearchParams }
    : recordSearch.additionalSearchParams;

  return (
    <Popover.Root
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.CloseTrigger aria-label="Close">
                <Icon asChild>
                  <MdClose />
                </Icon>
              </Popover.CloseTrigger>
              <Popover.Header id={titleId}>
                {recordSearch.label ?? 'Filter by record'}
              </Popover.Header>
              <Popover.Body minW="304px">
                <MCustomSelect
                  isLazy
                  clearable
                  useMainInputAsSearchInput
                  useHighlightedItemList
                  returnItem
                  itemTitle={itemTitle}
                  itemValue={itemValue}
                  infiniteScrolling
                  endpoint={recordSearch.endpoint}
                  additionalSearchParams={searchParams}
                  placeholder={recordSearch.placeholder ?? 'Search'}
                  MainInputComponent={MainSearchInputV2}
                  value={
                    selectedState
                      ? {
                          [itemValue]: selectedState.id,
                          [itemTitle]: selectedState.label,
                        }
                      : null
                  }
                  onChange={(item: any) => {
                    if (item && item[itemValue]) {
                      setSelectedState({
                        id: item[itemValue],
                        label: item[itemTitle] ?? item[itemValue],
                      });
                    } else {
                      setSelectedState(null);
                    }
                  }}
                />
              </Popover.Body>
              <Popover.Footer>
                <MFlex justifyContent="space-between">
                  <MButton
                    variant="tertiary"
                    size="sm"
                    onClick={handleResetFilters}
                    isDisabled={!hasChanges && !filtersApplied}
                  >
                    Reset
                  </MButton>
                  <MButton size="sm" onClick={handleApplyFilters}>
                    Apply
                  </MButton>
                </MFlex>
              </Popover.Footer>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
